"use client";

// الهويّة البصريّة (2026-10-05): المصدر الواحد لكلّ الشعارات وأيقونة التبويب.
// الملفّات في الخادم (data/theme/branding/<slot>.*) تُرفع من الإعدادات ← المظهر والألوان ←
// «الهويّة البصريّة»؛ ونسخة كلّ خانة في الثيم `branding` (0 = غير مرفوعة).
// البديل تلقائيّ: كلّ مكان يأخذ أخصّ شعار مرفوع له ثمّ الأعمّ، وأخيرًا الرئيسيّ من الخادم؛ لا نسخة
// ثانية في الواجهة (حُذفت public/images/logo.png بقرار المالك 2026-10-05) — إن تعذّر يُخفى الشعار.
// الفاتح/الداكن بـCSS (`.mc-brand-light/-dark` في globals.css) فلا وميض ولا خطأ hydration؛
// وبلا شعار داكن يظهر الفاتح على شارة فاتحة من لون نصّ الثيم (`.mc-brand-badge`).
import { useEffect, useState } from "react";
import { apiBase } from "@/lib/api";
import { useTheme } from "./useTheme";

export type BrandKey = "main" | "mainDark" | "nav" | "navDark" | "icon";
export type Branding = Record<BrandKey, number>;
export const brandMainUnversioned = () => `${apiBase()}/admin/theme/brand/main`;

export const BRAND_SLOTS: { key: BrandKey; slot: string; label: string; desc: string; accept: string }[] = [
  { key: "main", slot: "main", label: "الشعار الرئيسيّ", desc: "يُستعمل في كلّ مكان افتراضيًّا", accept: "image/png,image/jpeg,image/webp" },
  { key: "mainDark", slot: "main-dark", label: "الشعار للوضع الداكن", desc: "نسخة بنصّ فاتح — بدونها يظهر الرئيسيّ على شارة فاتحة", accept: "image/png,image/jpeg,image/webp" },
  { key: "nav", slot: "nav", label: "شعار القائمة الجانبيّة", desc: "اختياريّ — نسخة تناسب القائمة؛ بدونها الرئيسيّ", accept: "image/png,image/jpeg,image/webp" },
  { key: "navDark", slot: "nav-dark", label: "شعار القائمة للوضع الداكن", desc: "اختياريّ — بدونها: شعار القائمة ثمّ الداكن ثمّ الرئيسيّ", accept: "image/png,image/jpeg,image/webp" },
  { key: "icon", slot: "icon", label: "أيقونة التبويب (favicon)", desc: "اختياريّ — مربّعة، PNG أو ICO", accept: "image/png,image/x-icon,image/vnd.microsoft.icon,image/webp" },
];

const SLOT_OF: Record<BrandKey, string> = Object.fromEntries(BRAND_SLOTS.map((s) => [s.key, s.slot])) as Record<BrandKey, string>;

export function brandUrl(b: Partial<Branding> | undefined, key: BrandKey): string | null {
  const v = b?.[key];
  return v ? `${apiBase()}/admin/theme/brand/${SLOT_OF[key]}?v=${v}` : null;
}

/** الفاتح والداكن لمكان: «main» (الرئيسيّة والدخول واللوحة) أو «nav» (القائمة). */
export function brandPair(b: Partial<Branding> | undefined, place: "main" | "nav") {
  const main = brandUrl(b, "main") ?? brandMainUnversioned();
  const light = place === "nav" ? brandUrl(b, "nav") ?? main : main;
  const darkExact = place === "nav"
    ? brandUrl(b, "navDark") ?? (brandUrl(b, "nav") ? null : brandUrl(b, "mainDark"))
    : brandUrl(b, "mainDark");
  return { light, dark: darkExact ?? light, darkIsFallback: !darkExact };
}

const fallback = (e: React.SyntheticEvent<HTMLImageElement>) => {
  e.currentTarget.style.visibility = "hidden";   // لا شعار متاح — مساحته تبقى فلا يقفز التصميم
};

export function BrandLogo({ place, alt, className = "" }: { place: "main" | "nav"; alt: string; className?: string }) {
  const { settings } = useTheme();
  // الرابط يُحسب في المتصفّح وحده: الخادم يحسب apiBase() رابطًا داخليًّا (localhost:8000) والمتصفّح
  // رابط الدومين، وReact لا يصحّح سمة src بعد hydration ⇒ كان يطلب localhost من جهاز الزائر
  // (نافذة «Access other apps…» وشعار مكسور، 2026-10-05). قبل التركيب: مكان محجوز فارغ.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  if (!mounted) return <span aria-hidden="true" className={`inline-block ${className}`} />;
  const p = brandPair(settings?.branding, place);
  return (
    <>
      <img src={p.light} alt={alt} onError={fallback} className={`mc-brand-light ${className}`} />
      <img src={p.dark} alt={alt} onError={fallback}
        className={`mc-brand-dark ${p.darkIsFallback ? "mc-brand-badge" : ""} ${className}`} />
    </>
  );
}

/** أيقونة التبويب من الإعدادات (إن رُفعت)؛ وإلّا تبقى favicon.ico المضمَّنة. */
export function BrandFavicon() {
  const { settings } = useTheme();
  const href = brandUrl(settings?.branding, "icon");
  useEffect(() => {
    if (!href) return;
    let link = document.querySelector<HTMLLinkElement>("link#mc-favicon");
    if (!link) {
      link = document.createElement("link");
      link.id = "mc-favicon";
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [href]);
  return null;
}
